/**
 * questStore — Zustand store for quest state
 */

import { create } from 'zustand';
import {
  getQuests,
  claimReward,
  type PlayerQuestView,
  type QuestRewardResult,
} from '../services/QuestAPI';

interface QuestState {
  // Data
  quests: PlayerQuestView[];
  lastClaim: QuestRewardResult | null;

  // Loading states
  isLoading: boolean;
  isClaiming: boolean;
  error: string | null;

  // Computed (cached)
  claimableCount: number;

  // Actions
  fetchQuests: () => Promise<void>;
  claim: (questId: string) => Promise<QuestRewardResult | null>;
  clearLastClaim: () => void;
  clearError: () => void;
}

function countClaimable(quests: PlayerQuestView[]): number {
  return quests.filter(q => q.completed && !q.claimed).length;
}

export const useQuestStore = create<QuestState>((set, get) => ({
  quests: [],
  lastClaim: null,
  isLoading: false,
  isClaiming: false,
  error: null,
  claimableCount: 0,

  fetchQuests: async () => {
    set({ isLoading: true, error: null });
    try {
      const quests = await getQuests();
      set({
        quests,
        isLoading: false,
        claimableCount: countClaimable(quests),
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : '載入任務失敗';
      set({ isLoading: false, error: msg });
    }
  },

  claim: async (questId: string) => {
    set({ isClaiming: true, error: null });
    try {
      const result = await claimReward(questId);
      // Update the quest in the list
      const quests = get().quests.map(q =>
        q.id === questId ? { ...q, claimed: true } : q
      );
      set({
        isClaiming: false,
        lastClaim: result,
        quests,
        claimableCount: countClaimable(quests),
      });
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : '領取獎勵失敗';
      set({ isClaiming: false, error: msg });
      return null;
    }
  },

  clearLastClaim: () => { set({ lastClaim: null }); },
  clearError: () => { set({ error: null }); },
}));
